<template>
  <div class="profile-hero bg-finger">
    <div class="hero-spacer"></div>
  </div>

  <div class="profile-body">
    <!-- User info card -->
    <div class="profile-user-card card-priadi">
      <div class="profile-avatar">
        <i class="bi bi-person-fill"></i>
      </div>
      <p class="profile-name">{{ user.fullname }}</p>
      <p class="profile-email">{{ user.email }}</p>
    </div>

    <!-- Personal info -->
    <section class="profile-section">
      <p class="profile-section-title">{{ $t('personalInfo') }}</p>
      <div class="card-priadi profile-personal-info-card">
        <div class="profile-info-row">
          <div class="profile-info-icon">
            <i class="bi bi-calendar-week"></i>
          </div>
          <div class="profile-info-content">
            <span class="profile-info-label">{{ $t('birthdate') }}</span>
            <span class="profile-info-value">{{ convertDateID(user.birthdate) }}</span>
          </div>
        </div>
        <div class="profile-divider"></div>
        <div class="profile-info-row">
          <div class="profile-info-icon">
            <i class="bi bi-phone"></i>
          </div>
          <div class="profile-info-content">
            <span class="profile-info-label">{{ $t('phone') }}</span>
            <span class="profile-info-value">{{ user.phone_number }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Settings -->
    <section class="profile-section">
      <p class="profile-section-title">{{ $t('settings') }}</p>
      <div class="card-priadi profile-settings-card">
        <div class="profile-menu-item" @click="changePassword">
          <div class="profile-menu-left">
            <i class="bi bi-key"></i>
            <span>{{ $t('changePassword.title') }}</span>
          </div>
          <i class="bi bi-chevron-right"></i>
        </div>
        <div class="profile-menu-item" @click="openPrivacyPolicy">
          <div class="profile-menu-left">
            <i class="bi bi-shield-check"></i>
            <span>{{ $t('privacyPolicy') }}</span>
          </div>
          <i class="bi bi-chevron-right"></i>
        </div>
        <div class="profile-menu-item" @click="openTermsCondition">
          <div class="profile-menu-left">
            <i class="bi bi-file-earmark-text"></i>
            <span>{{ $t('termsCondition') }}</span>
          </div>
          <i class="bi bi-chevron-right"></i>
        </div>
        <div class="profile-menu-item" @click="openContact">
          <div class="profile-menu-left">
            <i class="bi bi-telephone-fill"></i>
            <span>{{ $t('contactUs') }}</span>
          </div>
          <i class="bi bi-chevron-right"></i>
        </div>
      </div>
    </section>

    <!-- Language -->
    <section class="profile-section">
      <p class="profile-section-title">{{ $t('language') }}</p>
      <div class="lang-grid">
        <button
          class="lang-card"
          :class="{ active: currentLang === 'en' }"
          @click="currentLang = 'en'">
          <span class="lang-flag">🇬🇧</span>
          <span class="lang-name">English</span>
        </button>
        <button
          class="lang-card"
          :class="{ active: currentLang === 'id' }"
          @click="currentLang = 'id'">
          <span class="lang-flag">🇮🇩</span>
          <span class="lang-name">Indonesia</span>
        </button>
        <button
          class="lang-card"
          :class="{ active: currentLang === 'zh' }"
          @click="currentLang = 'zh'">
          <span class="lang-flag">🇨🇳</span>
          <span class="lang-name">中文</span>
        </button>
      </div>
    </section>

    <!-- Logout -->
    <button class="btn-priadi-outline btn-priadi-block profile-logout" @click="logout">
      {{ $t('signout') }} <i class="bi bi-box-arrow-right"></i>
    </button>
  </div>

  <BottomNav />
</template>

<script>
import axios from 'axios'
import BottomNav from '../components/BottomNav'
import Swal from 'sweetalert2'
import { loadLocaleMessages } from '../i18n'

export default {
  name: 'Profile',
  components: { BottomNav },
  mounted() {
    document.body.classList.add('body-tinted')
  },
  unmounted() {
    document.body.classList.remove('body-tinted')
  },
  computed: {
    user() {
      return this.$store.getters.getUser
    },
    currentLang: {
      get() {
        return this.$store.getters.currentLocale
      },
      async set(lang) {
        this.$store.dispatch('setLocale', lang)
        await loadLocaleMessages(lang)
      },
    },
  },
  methods: {
    openContact() {
      window.open('https://wa.me/+628122082122', '_blank')
    },
    openPrivacyPolicy() {
      Swal.fire({
        title: this.$t('confirmation.title'),
        showCancelButton: true,
        showConfirmButton: true,
        text: this.$t('confirmation.prompt', { optional: this.$t('externalLink.prompt') }),
        confirmButtonText: this.$t('confirmation.yes'),
        cancelButtonText: this.$t('confirmation.cancel'),
      }).then((result) => {
        if (result.isConfirmed) {
          window.open('https://priadi.id/en/privacy-policy', '_blank')
        }
      })
    },
    openTermsCondition() {
      Swal.fire({
        title: this.$t('confirmation.title'),
        showCancelButton: true,
        showConfirmButton: true,
        text: this.$t('confirmation.prompt', { optional: this.$t('externalLink.prompt') }),
        confirmButtonText: this.$t('confirmation.yes'),
        cancelButtonText: this.$t('confirmation.cancel'),
      }).then((result) => {
        if (result.isConfirmed) {
          window.open('https://priadi.id/en/terms-and-conditions', '_blank')
        }
      })
    },
    changePassword() {
      this.$router.push('/change_password')
    },
    async logout() {
      try {
        await axios.post('/api/logout', {}, { withCredentials: true })
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        this.$store.dispatch('setAuth', false)
        this.$store.dispatch('setUser', { id: null, email: null })
        this.$router.push('/')
      }
    },
    convertDateID(date) {
      if (!date) return ''
      const [year, month, day] = date.split('-')
      const monthName = this.$t(`months.${month}`)
      return `${day} - ${monthName} - ${year}`
    },
  },
}
</script>

<style scoped>
/* Hero */
.profile-hero {
  position: relative;
}

/* Body content */
.profile-body {
  padding: 0 var(--space-4) calc(var(--space-16) + var(--space-10));
}

/* User card — gradient from PackageDetail */
.profile-user-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: var(--color-surface);
  background-image: linear-gradient(
    to bottom,
    rgba(var(--color-primary-rgb), 0.08) 0%,
    rgba(var(--color-primary-rgb), 0.09) 40%,
    var(--color-surface) 100%
  );
  border: none;
  border-radius: var(--radius-lg);
  padding: var(--space-8) var(--space-4) var(--space-6);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  margin-top: -4em;
  position: relative;
  z-index: 1;
  margin-bottom: var(--space-6);
}

.profile-avatar {
  width: 72px;
  height: 72px;
  border-radius: var(--radius-full);
  background-color: var(--color-primary);
  color: var(--color-text-inverse);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-2xl);
  margin-bottom: var(--space-3);
}

.profile-name {
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--color-text-primary);
  margin-bottom: var(--space-1);
}

.profile-email {
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  margin-bottom: 0;
}

/* Override card-priadi padding for settings card only */
.profile-personal-info-card {
  padding: 0;
}

.profile-settings-card {
  padding: 0;
}

/* Section */
.profile-section {
  margin-bottom: var(--space-6);
}

.profile-section-title {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: var(--space-3);
}

/* Info rows */
.profile-info-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
}

.profile-info-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  background-color: var(--color-primary-light);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-md);
  flex-shrink: 0;
}

.profile-info-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.profile-info-label {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.profile-info-value {
  font-size: var(--text-base);
  font-weight: var(--font-medium);
  color: var(--color-text-primary);
}

/* Menu items */
.profile-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4);
  cursor: pointer;
  transition: background-color var(--transition-base);
  -webkit-tap-highlight-color: transparent;
}

.profile-menu-item:active {
  background-color: var(--color-primary-light);
}

.profile-menu-left {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--text-base);
  color: var(--color-text-primary);
}

.profile-menu-left i {
  color: var(--color-primary);
  font-size: var(--text-md);
  width: 24px;
  text-align: center;
}

.profile-menu-item > i {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

/* Language grid */
.lang-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-3);
}

.lang-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-2);
  border-radius: var(--radius-md);
  border: 2px solid var(--color-border);
  background-color: var(--color-surface);
  cursor: pointer;
  transition: border-color var(--transition-base), background-color var(--transition-base);
  -webkit-tap-highlight-color: transparent;
  font-family: var(--font-family);
}

.lang-card:active {
  transform: scale(0.97);
}

.lang-card.active {
  border-color: var(--color-primary);
  background-color: rgba(var(--color-primary-rgb), 0.05);
}

.lang-flag {
  font-size: var(--text-2xl);
  line-height: 1;
}

.lang-name {
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  color: var(--color-text-primary);
}

/* Logout */
.profile-logout {
  border-radius: var(--radius-full);
  font-weight: var(--font-semibold);
  padding: var(--space-3) 0;
}
</style>
